import { PLAYER_X, standingGroundY } from "../game/constants";
import type { CharacterConfig } from "../game/types";

export type PlayerPose = "idle" | "aim" | "throw" | "cheer" | "miss";

const HAND: Record<"idle" | "aim" | "throw", { x: number; y: number }> = {
  idle: { x: 36, y: -50 },
  aim: { x: 24, y: -42 },
  throw: { x: 50, y: -60 },
};

export class Player {
  x = PLAYER_X;
  y = standingGroundY(PLAYER_X);
  pose: PlayerPose = "idle";
  throwTime = 0;
  reactTime = 0;
  cheerBig = false;
  lastMiss = false;
  private pending: "cheer" | "miss" | null = null;
  hand = { x: PLAYER_X + HAND.idle.x, y: standingGroundY(PLAYER_X) + HAND.idle.y };

  update(dt: number, time: number, reduceMotion: boolean): void {
    if (this.pose === "throw") {
      this.throwTime -= dt;
      if (this.throwTime <= 0) {
        if (this.pending && !reduceMotion) {
          this.pose = this.pending;
          this.reactTime = this.pending === "miss" && this.lastMiss ? 0.72 : this.cheerBig ? 0.58 : 0.42;
          this.pending = null;
        } else {
          this.pose = "idle";
        }
      }
    } else if (this.pose === "cheer" || this.pose === "miss") {
      this.reactTime -= dt;
      if (this.reactTime <= 0) this.pose = "idle";
    }
    this.y = standingGroundY(this.x);
    const key = this.pose === "aim" ? "aim" : this.pose === "throw" ? "throw" : "idle";
    const bob = reduceMotion ? 0 : Math.sin(time * 3) * 1.4;
    this.hand.x = this.x + HAND[key].x;
    this.hand.y = this.y + HAND[key].y + bob;
  }

  startAim(): void {
    this.pose = "aim";
    this.pending = null;
  }

  startThrow(): void {
    this.pose = "throw";
    this.throwTime = 0.2;
  }

  reactHit(combo: number, gold: boolean, reduceMotion: boolean): void {
    if (reduceMotion) return;
    this.cheerBig = gold || combo >= 4;
    this.pending = "cheer";
  }

  reactMiss(lastFrisbee: boolean, reduceMotion: boolean): void {
    if (reduceMotion) return;
    this.lastMiss = lastFrisbee;
    this.pending = "miss";
  }

  draw(ctx: CanvasRenderingContext2D, config: CharacterConfig, time: number, goldFrisbee = false, reduceMotion = false): void {
    drawCharacter(ctx, config, this.x, this.y, this.pose, time, goldFrisbee, reduceMotion, this.cheerBig);
  }
}

export function drawCharacter(
  ctx: CanvasRenderingContext2D,
  config: CharacterConfig,
  x: number,
  y: number,
  pose: PlayerPose,
  time: number,
  goldFrisbee = false,
  reduceMotion = false,
  cheerBig = false,
): void {
  const girl = config.id === "girl";
  const bob = reduceMotion ? 0 : Math.sin(time * 3) * 1.6;
  const jump = !reduceMotion && pose === "cheer" ? Math.abs(Math.sin(time * 14)) * (cheerBig ? 10 : 6) : 0;
  const sag = pose === "miss" ? 3 : 0;
  const aim = pose === "aim";
  const throwing = pose === "throw";
  const cheer = pose === "cheer";
  const miss = pose === "miss";
  ctx.save();
  ctx.translate(x, y + sag - jump + bob);

  ctx.fillStyle = "rgba(40, 30, 16, 0.22)";
  ctx.beginPath();
  ctx.ellipse(2, 4, girl ? 18 : 21, 5.5, 0, 0, Math.PI * 2);
  ctx.fill();

  const outfit = config.outfit;
  const dark = shade(outfit, 0.28);
  const light = tint(outfit, 0.28);
  const skin = config.skin;
  const hair = config.hair;
  const rightX = throwing ? 18 : aim ? 8 : cheer ? 16 : 12;
  const rightY = throwing ? -62 : aim ? -40 : cheer ? -78 : miss ? -44 : -50;
  const leftX = miss ? -20 : cheer ? -18 : -20;
  const leftY = cheer ? -78 : miss ? -36 : -32;

  drawLeg(ctx, skin, -9, -34, -13, -18, -14, -4);
  drawLeg(ctx, skin, 8, -34, 10, -18, 11, -4);
  drawFoot(ctx, skin, dark, -14, -2, girl);
  drawFoot(ctx, skin, dark, 10, -2, girl);

  drawClothes(ctx, girl, outfit, dark, light);

  drawArm(ctx, skin, -11, -50, leftX, leftY, miss ? -8 : 4);
  drawHand(ctx, skin, leftX, leftY - 2);
  drawArm(ctx, skin, 11, -50, rightX, rightY + 6, throwing ? 10 : 2);
  drawHand(ctx, skin, rightX + 2, rightY);
  drawDisc(ctx, rightX + 6, rightY - 2, goldFrisbee);

  ctx.fillStyle = skin;
  rounded(ctx, -4, -62, 8, 8, 3);
  ctx.fill();

  ctx.beginPath();
  ctx.ellipse(0, -76, girl ? 12.2 : 13.2, girl ? 15.2 : 16, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "rgba(255,255,255,0.2)";
  ctx.beginPath();
  ctx.ellipse(-4, -82, 5.2, 3.4, -0.35, 0, Math.PI * 2);
  ctx.fill();

  drawHair(ctx, hair, girl);
  drawFace(ctx, pose, cheerBig);

  ctx.fillStyle = "#1f6f86";
  ctx.font = "700 13px Trebuchet MS, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText(config.nickname, 0, 18);
  ctx.restore();
}

function drawClothes(
  ctx: CanvasRenderingContext2D,
  girl: boolean,
  outfit: string,
  dark: string,
  light: string,
): void {
  if (girl) {
    rounded(ctx, -13, -30, 26, 16, 7);
    ctx.fillStyle = dark;
    ctx.fill();
    ctx.fillStyle = light;
    ctx.fillRect(-7, -29, 14, 2);
    ctx.strokeStyle = shade(outfit, 0.4);
    ctx.lineWidth = 1.1;
    ctx.beginPath();
    ctx.moveTo(-4, -26);
    ctx.lineTo(-4, -16);
    ctx.moveTo(4, -26);
    ctx.lineTo(4, -16);
    ctx.stroke();
    rounded(ctx, -14, -56, 28, 28, 10);
    ctx.fillStyle = outfit;
    ctx.fill();
    ctx.fillStyle = dark;
    ctx.fillRect(-14, -32, 28, 4);
    ctx.strokeStyle = dark;
    ctx.lineWidth = 3.4;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(-9, -56);
    ctx.lineTo(-12, -40);
    ctx.moveTo(9, -56);
    ctx.lineTo(12, -40);
    ctx.stroke();
    ctx.strokeStyle = light;
    ctx.lineWidth = 1.3;
    ctx.beginPath();
    ctx.moveTo(-8, -50);
    ctx.lineTo(-8, -34);
    ctx.moveTo(8, -50);
    ctx.lineTo(8, -34);
    ctx.stroke();
    drawWaves(ctx, light, -8, -48, 16);
    drawMark(ctx, light, 0, -42);
  } else {
    rounded(ctx, -15, -30, 30, 18, 7);
    ctx.fillStyle = dark;
    ctx.fill();
    ctx.fillStyle = light;
    ctx.fillRect(-5, -28, 10, 2);
    ctx.strokeStyle = shade(outfit, 0.38);
    ctx.lineWidth = 1.2;
    ctx.strokeRect(-11, -22, 7, 7);
    ctx.beginPath();
    ctx.moveTo(-3, -28);
    ctx.lineTo(0, -22);
    ctx.lineTo(3, -28);
    ctx.stroke();
    rounded(ctx, -16, -56, 32, 30, 9);
    ctx.fillStyle = outfit;
    ctx.fill();
    ctx.fillStyle = dark;
    ctx.fillRect(-16, -31, 32, 5);
    ctx.fillStyle = shade(outfit, 0.18);
    rounded(ctx, -24, -54, 10, 16, 5);
    ctx.fill();
    rounded(ctx, 14, -54, 10, 16, 5);
    ctx.fill();
    ctx.strokeStyle = dark;
    ctx.lineWidth = 1.3;
    ctx.strokeRect(-23, -53, 8, 14);
    ctx.strokeRect(15, -53, 8, 14);
    drawWaves(ctx, light, -9, -46, 18);
    drawMark(ctx, light, 0, -43);
    ctx.fillStyle = "rgba(255,255,255,0.22)";
    ctx.fillRect(-12, -54, 24, 3);
  }
}

function drawWaves(ctx: CanvasRenderingContext2D, color: string, x: number, y: number, w: number): void {
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.15;
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.quadraticCurveTo(x + w * 0.25, y - 3, x + w * 0.5, y);
  ctx.quadraticCurveTo(x + w * 0.75, y + 3, x + w, y);
  ctx.stroke();
}

function drawMark(ctx: CanvasRenderingContext2D, color: string, x: number, y: number): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y, 3.3, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#fff";
  ctx.globalAlpha = 0.55;
  ctx.beginPath();
  ctx.ellipse(x, y, 2.4, 1.1, -0.4, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 1;
}

function drawHair(ctx: CanvasRenderingContext2D, color: string, girl: boolean): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.ellipse(0, -86, girl ? 13.5 : 13, girl ? 9 : 8, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(0, -76, girl ? 13 : 13.6, Math.PI * 1.08, Math.PI * 1.92);
  ctx.quadraticCurveTo(0, -98, girl ? -13 : -13.6, -80);
  ctx.fill();
  if (girl) {
    ctx.beginPath();
    ctx.ellipse(-13, -70, 5.5, 14, 0.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(13, -70, 5.5, 14, -0.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(-8, -88, 5.2, 4.4, -0.45, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(8, -88, 5.2, 4.4, 0.45, 0, Math.PI * 2);
    ctx.fill();
  } else {
    ctx.beginPath();
    ctx.ellipse(-12, -78, 3.6, 7, 0.35, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(12, -78, 3.6, 6.6, -0.35, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawFace(ctx: CanvasRenderingContext2D, pose: PlayerPose, cheerBig: boolean): void {
  const miss = pose === "miss";
  const cheer = pose === "cheer";
  const brow = miss ? 2.2 : cheer ? -1.6 : 0;
  ctx.fillStyle = "#1b2430";
  ctx.beginPath();
  ctx.ellipse(-4.8, -78, 2.2, cheer ? 2.7 : 2.3, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(4.8, -78, 2.2, cheer ? 2.7 : 2.3, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#fff";
  ctx.beginPath();
  ctx.arc(-5.4, -78.8, 0.75, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(4.2, -78.8, 0.75, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "#1b2430";
  ctx.lineWidth = 1.45;
  ctx.beginPath();
  ctx.moveTo(-8, -84 + brow);
  ctx.lineTo(-3, -83 + brow);
  ctx.moveTo(8, -84 + brow);
  ctx.lineTo(3, -83 + (miss ? 2.2 : brow));
  ctx.stroke();
  ctx.fillStyle = "rgba(80, 40, 30, 0.28)";
  ctx.beginPath();
  ctx.ellipse(0, -74, 1.7, 1.15, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = miss ? "#8a4a4a" : "#c56b6b";
  ctx.lineWidth = cheerBig && cheer ? 2.3 : 1.7;
  ctx.beginPath();
  if (miss) {
    ctx.arc(0, -68, 4.6, Math.PI + 0.35, -0.35, true);
  } else {
    ctx.arc(0, -72, cheer ? 5.5 : 4.3, 0.18, Math.PI - 0.18);
  }
  ctx.stroke();
}

function drawLimb(ctx: CanvasRenderingContext2D, color: string, x0: number, y0: number, x1: number, y1: number, w: number): void {
  ctx.strokeStyle = color;
  ctx.lineWidth = w;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(x0, y0);
  ctx.lineTo(x1, y1);
  ctx.stroke();
}

function drawArm(
  ctx: CanvasRenderingContext2D,
  color: string,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  bend: number,
): void {
  const mx = (x0 + x1) / 2 + bend * 0.15;
  const my = (y0 + y1) / 2 + Math.abs(bend) * 0.35 + 3;
  drawLimb(ctx, color, x0, y0, mx, my, 6.4);
  drawLimb(ctx, color, mx, my, x1, y1, 6);
}

function drawLeg(
  ctx: CanvasRenderingContext2D,
  color: string,
  hipX: number,
  hipY: number,
  kneeX: number,
  kneeY: number,
  footX: number,
  footY: number,
): void {
  drawLimb(ctx, color, hipX, hipY, kneeX, kneeY, 7.2);
  drawLimb(ctx, color, kneeX, kneeY, footX, footY, 6.6);
}

function drawHand(ctx: CanvasRenderingContext2D, skin: string, x: number, y: number): void {
  ctx.fillStyle = skin;
  ctx.beginPath();
  ctx.ellipse(x, y, 5.2, 4.4, 0.2, 0, Math.PI * 2);
  ctx.fill();
}

function drawFoot(ctx: CanvasRenderingContext2D, skin: string, sole: string, x: number, y: number, girl: boolean): void {
  ctx.fillStyle = sole;
  ctx.beginPath();
  ctx.ellipse(x + 4, y + 2, girl ? 8 : 9, 3.4, 0.1, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = skin;
  ctx.beginPath();
  ctx.ellipse(x + 2, y, 5, 3, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = sole;
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.moveTo(x - 1, y - 1);
  ctx.lineTo(x + 7, y + 1);
  ctx.stroke();
}

function drawDisc(ctx: CanvasRenderingContext2D, x: number, y: number, gold: boolean): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(1, 0.38);
  ctx.beginPath();
  ctx.arc(0, 0, 9, 0, Math.PI * 2);
  ctx.fillStyle = gold ? "#ffe566" : "#ff3b3b";
  ctx.fill();
  ctx.strokeStyle = gold ? "#fff3b0" : "rgba(255,255,255,0.5)";
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.restore();
}

function rounded(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function hexToRgb(hex: string): number[] {
  const n = hex.replace("#", "");
  return [parseInt(n.slice(0, 2), 16), parseInt(n.slice(2, 4), 16), parseInt(n.slice(4, 6), 16)];
}

function shade(hex: string, t: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgb(${Math.round((r ?? 0) * (1 - t))}, ${Math.round((g ?? 0) * (1 - t))}, ${Math.round((b ?? 0) * (1 - t))})`;
}

function tint(hex: string, t: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgb(${Math.round((r ?? 0) + (255 - (r ?? 0)) * t)}, ${Math.round((g ?? 0) + (255 - (g ?? 0)) * t)}, ${Math.round((b ?? 0) + (255 - (b ?? 0)) * t)})`;
}
